import React from "react";
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, Stack, TableHead, Checkbox, TablePagination, TableRow, Typography,
  TextField, Breadcrumbs, Link, Button, InputAdornment,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import SearchIcon from "@mui/icons-material/Search";
import { useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';

const TableComponent = ({
  FiltersJsx,
  columns,
  rows,
  CountryJsx,
  handleRowDoubleClick,
  handleSearchChange,
  handleChangePage,
  handleChangeRowsPerPage,
  handleClick,
  handleHomeClick,
  handleLocationclick,
  filteredRows,
  title,
  titleMaster,
  handleIconClick,
  isEditMode,
  setIsEditMode,
  selectedRoomBedIds,
  setSelectedRoomBedIds,
  onCellEdit,
  onSave,
  editable,
  onCancel,
  isLoading,
  handleDeleteClick,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  searchTerms,
}) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(17);
  const location = useLocation();
  const permissions = location.state?.permissions || [];

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = filteredRows?.map(row => row.RoomBedId);
      setSelectedRoomBedIds(newSelected);
      return;
    }
    setSelectedRoomBedIds([]);
  };

  const handleCheckboxClick = (event, roomBedId) => {
    event.stopPropagation();
    const selectedIndex = selectedRoomBedIds.indexOf(roomBedId);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRoomBedIds, roomBedId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRoomBedIds.slice(1));
    } else if (selectedIndex === selectedRoomBedIds.length - 1) {
      newSelected = newSelected.concat(selectedRoomBedIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedRoomBedIds.slice(0, selectedIndex),
        selectedRoomBedIds.slice(selectedIndex + 1)
      );
    }
    setSelectedRoomBedIds(newSelected);
  };

  
const getTooltipContent = (row, columnId, value) => {
  // Only show tooltip for B, P, O status
  if (!['B', 'P', 'O', 'L'].includes(value)) return '';

  // Extract year and month from columnId (format: "2024_January")
  const [year, month] = columnId.split('_');
  
  if (!year || !month) return '';

  // Find the specific booking for this month and year
  const specificBooking = row.bookings?.find(booking => 
    booking.YearNm === parseInt(year) && booking.MonthNm === month
  );

  // If specific booking found, show those details
  if (specificBooking) {
    return `Resident Name: ${specificBooking.ResidentName || 'N/A'}\nBook No: ${specificBooking.BookNo || 'N/A'}`;
  }

  // Fallback to row-level booking details if specific not found
  return `Resident Name: ${row.ResidentName || 'N/A'}\nBook No: ${row.BookNo || 'N/A'}`;
};


  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleSaveClick = () => {
    onSave();
  };

  const handleCancelClick = () => {
    setIsEditMode(false);
    setSelectedRoomBedIds([]);
    onCancel();
  };

  const isSelected = (roomBedId) => selectedRoomBedIds.indexOf(roomBedId) !== -1;

  const handleLocationNavigation = () => {
    handleLocationclick(permissions);
  };

  const totalPages = Math.ceil(filteredRows?.length / rowsPerPage);

  const isCellEditable = (roomBedId, columnId, value) => {
    if (!isEditMode || !selectedRoomBedIds.includes(roomBedId)) return false;

    const isBaseColumn = ['RoomNo', 'RoombedName', 'RoomTypeName', 'RoomBedTypeName', 'RoomViewNAme'].includes(columnId);
    if (isBaseColumn) return false;

    if (columnId.startsWith('year_')) return false;
    if (['B', 'P', 'C'].includes(value)) return false;
    return true;
  };

  const handleCellChange = (event, roomBedId, columnId, currentValue) => {
    const newValue = event.target.value.toUpperCase();

    if (newValue === '') {
      onCellEdit(roomBedId, columnId, '', currentValue);
      return;
    }
    if (['A', 'M', 'R'].includes(newValue)) {
      onCellEdit(roomBedId, columnId, newValue, currentValue);
    }
  };

  const getCellBackgroundColor = (value, originalValue) => {
    const effectiveValue = value || originalValue;
    if (!effectiveValue) return null;

    const upperValue = String(effectiveValue).toUpperCase();

    switch (upperValue) {
      case 'A':
        return '#046A38';
      case 'C':
        return '#c971bb';
      case 'O':
        return '#f05c50';
      case 'L':
        return '#a50cec ';
      case 'B':
        return '#635bff';
      case 'M':
        return '#800000';
      case 'R':
        return '#47a1ba';
      case 'P':
        return '#FF671F';
      default:
        return null;
    }
  };

  const renderCell = (roomBedId, columnId, value, originalValue) => {
    if (isCellEditable(roomBedId, columnId, value)) {
      const backgroundColor = getCellBackgroundColor(value, originalValue);
      return (
        <TextField
          value={value || ''}
          onChange={(e) => handleCellChange(e, roomBedId, columnId, originalValue)}
          size="small"
          inputProps={{
            style: {
              textAlign: 'center',
              padding: '0.1px',
              fontSize: '12px',
              backgroundColor: backgroundColor,
              color: backgroundColor ? '#ffffff' : 'inherit',
            },
            maxLength: 1
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
              width: '30px',
              height: '25px'
            }
          }}
        />
      );
    }

    const backgroundColor = getCellBackgroundColor(value, originalValue);
    return (
      <div style={{
        backgroundColor,
        color: backgroundColor ? '#ffffff' : 'inherit',
        padding: '0px',
        fontWeight: backgroundColor ? 'bold' : 'normal',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {value || ''}
      </div>
    );
  };

  const allSelected = filteredRows?.length > 0 && selectedRoomBedIds?.length === filteredRows?.length;
  const indeterminate = selectedRoomBedIds?.length > 0 && selectedRoomBedIds?.length < filteredRows?.length;

  return (
    <Box
      sx={{
        maxWidth: "95vw",
        overflowX: "hidden",
        backgroundColor: "#f9f9f9",
        minHeight: "80vh",
      }}
    >
      <ToastContainer />
      <Box
        sx={{
          backgroundColor: "#ffffff",
          padding: "5px 22px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          borderRadius: "12px",
          margin: "10px auto",
          maxWidth: "95%",
        }}
      >
        <Breadcrumbs
          aria-label="breadcrumb"
          separator="›"
          sx={{ fontSize: "16px" }}
        >
          <Link
            onClick={handleHomeClick}
            underline="hover"
            color="#635bff"
            sx={{ cursor: "pointer" }}
          >
            Home
          </Link>

          <Typography
            color="#635bff"
            onClick={handleLocationclick}
            sx={{ cursor: "pointer", fontWeight: "bold" }}
          >
            {title}
          </Typography>
          <Typography color="text.primary" sx={{ fontWeight: "bold" }}>
            {titleMaster}
          </Typography>
        </Breadcrumbs>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "end",
            gap: "8px",
            minWidth: "200px",
          }}
        >
          <TextField
            placeholder="Searh Here..."
            variant="outlined"
            size="small"
            sx={{
              width: "200px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "5px",
                backgroundColor: "#f9f9f9",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={(e) => handleSearchChange("generalSearch", e.target.value)}
            helperText={searchTerms.generalSearch?.length > 0 && searchTerms.generalSearch.length < 3 ?
              "Type at least 3 characters to search" : ""}
          />
          {FiltersJsx ? FiltersJsx : null}
        </Box>
      </Box>

      <Paper
        sx={{
          width: "95%",
          overflow: "hidden",
          margin: "10px auto",
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          padding: "20px",
          position: "relative",
        }}
      >
        <TableContainer sx={{ maxHeight: 530 }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow sx={{ "& > th": { padding: "2px" } }}>
                <TableCell padding="checkbox" sx={{ backgroundColor: "#dddbff" }}>
                  <Checkbox
                    indeterminate={indeterminate}
                    checked={allSelected}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={{
                      backgroundColor: "#dddbff",
                      color: "#333",
                      fontWeight: "600",
                      padding: "4px 2px 6px 5px !important",
                      border: "0.5px solid #ddd",
                      borderBottom: "2px solid #ddd",
                      minWidth: column.minWidth || 'auto',    // Column की minWidth apply करें
                      width: column.width || 'auto',          // Column की width apply करें
                      maxWidth: column.maxWidth || 'none',
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ borderBottom: "0.5px solid #ddd" }}
                    >
                      {column.label}
                    </Typography>
                    <TextField
                      size="small"
                      variant="outlined"
                      onChange={(e) =>
                        handleSearchChange(column.id, e.target.value)
                      }
                      sx={{
                        mt: 1,
                        width: '100%',
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          backgroundColor: "#f9f9f9",
                          fontSize: "0.8rem",
                          height: "26px",
                        },
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredRows
                .map((row) => {  // Remove the .slice() method here
                  const isItemSelected = isSelected(row.RoomBedId);
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={row.RoomBedId}
                      onDoubleClick={() => handleRowDoubleClick(row)}
                      selected={isItemSelected}
                      sx={{
                        "& > td": {
                          padding: "0px",
                          border: "0.5px solid #ddd",
                        },
                        "&:hover": {
                          backgroundColor: "#f1f1f1",
                          cursor: "pointer",
                        },
                        transition: "background-color 0.3s ease",
                      }}
                    >
                      <TableCell padding="checkbox" sx={{
                        textAlign: 'center',
                        '& .MuiCheckbox-root': {
                          padding: '0',
                          display: 'flex',
                          margin: '0'
                        }
                      }}>
                        <Checkbox
                          checked={isItemSelected}
                          onClick={(event) => handleCheckboxClick(event, row.RoomBedId)}
                        />
                      </TableCell>
                      {CountryJsx && <CountryJsx row={row} />}
                      {CountryJsx
                        ? columns.slice(1).map((column) => {
                          const value = row[column.id];
                          const backgroundColor = getCellBackgroundColor(value);

                          return (
                            <Tooltip
  title={getTooltipContent(row, column.id, value)}
  arrow
  placement="top"
  key={column.id}
>
  <TableCell
    align={column.align}
    sx={{
      backgroundColor: backgroundColor,
      color: backgroundColor ? '#ffffff' : 'inherit',
      fontWeight: backgroundColor ? 'bold' : 'normal',
      textAlign: 'center',
      verticalAlign: 'middle',
      minWidth: column.minWidth || 'auto',
      width: column.width || 'auto',
      maxWidth: column.maxWidth || 'none',
    }}
  >
    {renderCell(row.RoomBedId, column.id, value?.BedStatus || value || '', row[`${column.id}_original`])}
  </TableCell>
</Tooltip>
                          );
                        })
                        : columns.map((column) => {
                          const value = row[column.id];
                          const backgroundColor = getCellBackgroundColor(value);

                          return (
                            <Tooltip
  title={getTooltipContent(row, column.id, value)}
  arrow
  placement="top"
  key={column.id}
>
  <TableCell
    align={column.align}
    sx={{
      backgroundColor: backgroundColor,
      color: backgroundColor ? '#ffffff' : 'inherit',
      fontWeight: backgroundColor ? 'bold' : 'normal',
      textAlign: 'center',
      verticalAlign: 'middle',
      minWidth: column.minWidth || 'auto',
      width: column.width || 'auto',
      maxWidth: column.maxWidth || 'none',
    }}
  >
    {renderCell(row.RoomBedId, column.id, value?.BedStatus || value || '', row[`${column.id}_original`])}
  </TableCell>
</Tooltip>
                          );
                        })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        {isLoading && (
          <Box sx={{
            position: "absolute",
            top: 100,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 1,
          }}>
            <CircularProgress sx={{ color: "#635bff" }} />
          </Box>
        )}

        <TablePagination
          rowsPerPageOptions={[500, 1000, 3000, 5000, 10000]}
          component="div"
          count={pagination.totalRecords}
          rowsPerPage={pagination.pageSize}
          page={pagination.currentPage - 1} // MUI uses 0-based index
          onPageChange={(event, newPage) => {
            onPageChange(newPage + 1); // Convert to 1-based index
          }}
          onRowsPerPageChange={(event) => {
            onRowsPerPageChange(parseInt(event.target.value, 10));
          }}
          sx={{
            backgroundColor: "#dddbff",
            borderTop: "1px solid #ddd",
            color: "#333",
            borderRadius: "0 0 12px 12px",
            "& .MuiToolbar-root": {
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              width: "100%",
              flexWrap: "wrap",
            },
            "& .MuiTablePagination-displayedRows": {
              display: "none",
            },
            "& .MuiTablePagination-actions": {
              display: "none",
            },
            "& .MuiTablePagination-selectLabel": {
              marginBottom: 2,
            },
            "& .MuiTablePagination-select": {
              marginBottom: 0,
            },
          }}
          ActionsComponent={() => (
            <Stack direction="row" alignItems="center">
              <Typography
                variant="body2"
                sx={{ position: 'absolute', left: '20px', fontWeight: 'bold', color: "#333", }}
              >
                Total Records: {filteredRows.length}
              </Typography>
              <Typography
                variant="body2"
                sx={{ position: 'absolute', left: '130px', fontWeight: 'bold', color: "#333", }}
              >
                Selected Count: {selectedRoomBedIds.length}
              </Typography>

              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={(event, newPage) => onPageChange(newPage)}
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  "& .MuiPaginationItem-root.Mui-selected": {
                    backgroundColor: "#635bff",
                    color: "#ffffff",
                  },
                }}
              />
            </Stack>
          )}
        />
      </Paper>
      {!isLoading && rows.length === 0 && (
        <Box sx={{
          width: "90%",
          margin: "20px auto",
          display: "flex",
          justifyContent: "center",
          color: "red",
        }}>
          No Record Found...
        </Box>
      )}
    </Box>
  );
};

export default TableComponent;